import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, AlertCircle, Clock, Users, X, Copy, Check, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import NeedsReviewPanel from '../components/NeedsReviewPanel';
import './FollowUps.css';

// =============================================================
// FOLLOW-UPS PAGE — Wave 2 (full functionality)
// All action buttons wired to real endpoints.
// =============================================================

const FILTER_OPTIONS = [
  { value: 'today_overdue', label: 'Today + Overdue' },
  { value: 'today',         label: 'Today' },
  { value: 'this_week',     label: 'This Week' },
  { value: 'all_pending',   label: 'All Pending' }
];

const TYPE_TAG_CLASS = {
  type_1: 'fu-tag fu-tag-t1',
  type_2: 'fu-tag fu-tag-t2',
  type_3: 'fu-tag fu-tag-t3'
};

const TYPE_TAG_LABEL = {
  type_1: 'T1',
  type_2: 'T2',
  type_3: 'T3'
};

const SNOOZE_OPTIONS = [
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 'custom', label: 'Custom' }
];

const STOP_OPTIONS = [
  { value: 'replied', label: 'Replied (switch to conversation)' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'dead', label: 'Dead' }
];

export default function FollowUps() {
  const { isReadOnly } = useAuth();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isBDE = user.role === 'bde';
  const isTeamLead = user.role === 'team_lead';

  const [filter, setFilter] = useState('today_overdue');
  const defaultScope = isBDE ? 'mine' : (isTeamLead ? 'mine' : 'all');
  const [scope, setScope] = useState(defaultScope);
  const [searchQuery, setSearchQuery] = useState('');

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ overdue: 0, today: 0, upcoming_week: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [generateModal, setGenerateModal] = useState(null);
  const [markSentModal, setMarkSentModal] = useState(null);
  const [repliedModal, setRepliedModal] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/follow-ups/dashboard', {
        params: { filter, scope: isBDE ? 'mine' : scope }
      });
      setItems(res.data.items || []);
      setCounts(res.data.counts || { overdue: 0, today: 0, upcoming_week: 0 });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load follow-ups.');
    } finally {
      setLoading(false);
    }
  }, [filter, scope, isBDE]);

  useEffect(() => {
    fetchDashboard();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchDashboard, 60000);

    return () => clearInterval(interval);
  }, [fetchDashboard]);

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.fu-dropdown-wrap')) setOpenDropdown(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const renderStatus = (item) => {
    if (item.days_overdue > 0) {
      return (
        <span className="fu-status fu-status-overdue">
          Overdue by {item.days_overdue} day{item.days_overdue > 1 ? 's' : ''}
        </span>
      );
    }
    if (item.days_overdue === 0) {
      return <span className="fu-status fu-status-today">Due today</span>;
    }
    return (
      <span className="fu-status fu-status-upcoming">
        In {Math.abs(item.days_overdue)} day{Math.abs(item.days_overdue) > 1 ? 's' : ''}
      </span>
    );
  };

  // ---------------------------------------------------------
  // GENERATE
  // ---------------------------------------------------------
  const openGenerate = async (item) => {
    setGenerateModal({ followUp: item, channel: 'linkedin', prompt: '', loading: true, copied: false });
    try {
      const res = await api.get(`/follow-ups/${item.follow_up_id}/generate-payload`, {
        params: { channel: 'linkedin' }
      });
      setGenerateModal({
        followUp: item,
        channel: 'linkedin',
        prompt: res.data.prompt,
        loading: false,
        copied: false
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load prompt.');
      setGenerateModal(null);
    }
  };

  const switchGenerateChannel = async (channel) => {
    if (!generateModal) return;
    setGenerateModal({ ...generateModal, channel, loading: true });
    try {
      const res = await api.get(`/follow-ups/${generateModal.followUp.follow_up_id}/generate-payload`, {
        params: { channel }
      });
      setGenerateModal({
        ...generateModal,
        channel,
        prompt: res.data.prompt,
        loading: false,
        copied: false
      });
    } catch (err) {
      toast.error('Failed to switch channel.');
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generateModal.prompt);
      setGenerateModal({ ...generateModal, copied: true });
      toast.success('Prompt copied to clipboard.');
      setTimeout(() => {
        setGenerateModal(prev => prev ? { ...prev, copied: false } : null);
      }, 2000);
    } catch (err) {
      toast.error('Could not copy. Please select and copy manually.');
    }
  };

  // ---------------------------------------------------------
  // MARK SENT
  // ---------------------------------------------------------
  const openMarkSent = (item) => {
    setMarkSentModal({ followUp: item, channel: 'linkedin', messageText: '' });
  };

  const submitMarkSent = async () => {
    const { followUp, channel, messageText } = markSentModal;
    try {
      const res = await api.post(`/follow-ups/${followUp.follow_up_id}/mark-sent`, {
        channel,
        message_text: messageText || undefined
      });
      if (res.data.appended_to_notes) {
        toast.success('Marked sent. Message added to lead notes.');
      } else {
        toast.success('Marked sent.');
        toast('Tip: paste the message next time so the next follow-up has full context.', { duration: 5000 });
      }
      setMarkSentModal(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark sent.');
    }
  };

  // ---------------------------------------------------------
  // SNOOZE
  // ---------------------------------------------------------
  const snooze = async (item, days) => {
    setOpenDropdown(null);
    try {
      await api.post(`/follow-ups/${item.follow_up_id}/snooze`, { days });
      toast.success(`Snoozed for ${days} days.`);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to snooze.');
    }
  };

  const snoozeCustom = (item) => {
    setOpenDropdown(null);
    const input = window.prompt('Snooze for how many days? (1-60)');
    if (!input) return;
    const days = parseInt(input, 10);
    if (!Number.isInteger(days) || days < 1 || days > 60) {
      toast.error('Enter a number between 1 and 60.');
      return;
    }
    snooze(item, days);
  };

  // ---------------------------------------------------------
  // STOP
  // ---------------------------------------------------------
  const stopSequence = async (item, reason) => {
    setOpenDropdown(null);
    if (reason === 'replied') {
      setRepliedModal({ followUp: item, channel: 'linkedin', replyText: '' });
      return;
    }
    const confirmMsg = reason === 'not_interested'
      ? `Mark "${item.prospect_name}" as not interested? This stops the sequence.`
      : `Mark "${item.prospect_name}" as dead? This stops the sequence.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post(`/leads/${item.lead_id}/stop-sequence`, { reason });
      toast.success('Sequence stopped.');
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop sequence.');
    }
  };

  const submitReplied = async () => {
    const { followUp, channel, replyText } = repliedModal;
    if (!replyText.trim()) {
      toast.error('Please paste the prospect reply.');
      return;
    }
    try {
      await api.post(`/follow-ups/${followUp.follow_up_id}/log-prospect-reply`, {
        channel,
        reply_text: replyText
      });
      toast.success('Reply logged. New follow-up scheduled.');
      setRepliedModal(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to log reply.');
    }
  };

  // Client-side search: filter visible items by prospect_name or company.
  // Substring match, case-insensitive. When the search box is empty,
  // shows the full unfiltered list. Filtering happens AFTER the API
  // call — the server still returns everything for the chosen filter
  // + scope; we just narrow what's displayed.
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = trimmedQuery
    ? items.filter(item => {
        return (
          (item.prospect_name || '').toLowerCase().includes(trimmedQuery) ||
          (item.company || '').toLowerCase().includes(trimmedQuery)
        );
      })
    : items;

  return (
    <div className="follow-ups-page">
      <div className="fu-header">
        <h1><CalendarDays size={22} /> Follow-ups</h1>
      </div>

      <NeedsReviewPanel />

      <div className="fu-controls">
        <div className="fu-filter-pills">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`fu-pill ${filter === opt.value ? 'active' : ''}`}
              onClick={() => setFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {isTeamLead && (
          <div className="fu-scope-toggle">
            <button
              className={`fu-toggle ${scope === 'mine' ? 'active' : ''}`}
              onClick={() => setScope('mine')}
            >
              Mine
            </button>
            <button
              className={`fu-toggle ${scope === 'all' ? 'active' : ''}`}
              onClick={() => setScope('all')}
            >
              <Users size={14} /> All Team
            </button>
          </div>
        )}
      </div>

      {/* Search row — filters the visible list by prospect name or company */}
      <div className="fu-search-row">
        <div className="fu-search-wrap">
          <Search size={14} className="fu-search-icon" />
          <input
            type="text"
            className="fu-search-input"
            placeholder="Search by prospect name or company…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="fu-search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {trimmedQuery && (
          <div className="fu-search-meta">
            Showing {filteredItems.length} of {items.length}
          </div>
        )}
      </div>

      <div className="fu-counts">
        <div className="fu-count-card fu-count-overdue">
          <div className="fu-count-icon"><AlertCircle size={18} /></div>
          <div className="fu-count-body">
            <div className="fu-count-num">{counts.overdue}</div>
            <div className="fu-count-label">Overdue</div>
          </div>
        </div>
        <div className="fu-count-card">
          <div className="fu-count-icon"><Clock size={18} /></div>
          <div className="fu-count-body">
            <div className="fu-count-num">{counts.today}</div>
            <div className="fu-count-label">Today</div>
          </div>
        </div>
        <div className="fu-count-card fu-count-upcoming">
          <div className="fu-count-icon"><CalendarDays size={18} /></div>
          <div className="fu-count-body">
            <div className="fu-count-num">{counts.upcoming_week}</div>
            <div className="fu-count-label">Upcoming this week</div>
          </div>
        </div>
      </div>

      {loading && <div className="fu-message">Loading…</div>}
      {error && <div className="fu-message fu-error">{error}</div>}
      {!loading && !error && filteredItems.length === 0 && (
        <div className="fu-message">
          {trimmedQuery
            ? `No follow-ups match "${searchQuery.trim()}".`
            : 'No follow-ups for this view.'}
        </div>
      )}

      {!loading && !error && filteredItems.length > 0 && (
        <div className="fu-list">
          {filteredItems.map(item => (
            <div
              className="fu-row"
              key={item.follow_up_id}
              id={`fu-row-${item.follow_up_id}`}
            >
              <div className="fu-row-main">
                <span className={TYPE_TAG_CLASS[item.follow_up_type] || 'fu-tag'}>
                  {TYPE_TAG_LABEL[item.follow_up_type] || item.follow_up_type}
                </span>
                <div className="fu-row-text">
                  <Link to={`/leads/${item.lead_id}`} className="fu-prospect">
                    {item.prospect_name}
                  </Link>
                  <span className="fu-company">{item.company}</span>
                  {!isBDE && scope === 'all' && (
                    <span className="fu-bde">@{item.bde_name}</span>
                  )}
                </div>
                <div className="fu-row-meta">
                  <span className="fu-label">{item.follow_up_label}</span>
                  {renderStatus(item)}
                </div>
              </div>
              {!isReadOnly && (
                <div className="fu-row-actions">
                  <button
                    className="fu-action fu-action-primary"
                    onClick={() => openGenerate(item)}
                  >
                    Generate
                  </button>
                  <button
                    className="fu-action"
                    onClick={() => openMarkSent(item)}
                  >
                    Mark sent
                  </button>

                  <div className="fu-dropdown-wrap">
                    <button
                      className="fu-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === `snooze-${item.follow_up_id}` ? null : `snooze-${item.follow_up_id}`);
                      }}
                    >
                      Snooze ▾
                    </button>
                    {openDropdown === `snooze-${item.follow_up_id}` && (
                      <div className="fu-dropdown">
                        {SNOOZE_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            className="fu-dropdown-item"
                            onClick={() => opt.value === 'custom' ? snoozeCustom(item) : snooze(item, opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="fu-dropdown-wrap">
                    <button
                      className="fu-action fu-action-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === `stop-${item.follow_up_id}` ? null : `stop-${item.follow_up_id}`);
                      }}
                    >
                      Stop ▾
                    </button>
                    {openDropdown === `stop-${item.follow_up_id}` && (
                      <div className="fu-dropdown">
                        {STOP_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            className="fu-dropdown-item"
                            onClick={() => stopSequence(item, opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* GENERATE MODAL */}
      {generateModal && (
        <div className="fu-modal-backdrop" onClick={() => setGenerateModal(null)}>
          <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fu-modal-header">
              <div>
                <h2>Generate Message</h2>
                <div className="fu-modal-sub">
                  {generateModal.followUp.prospect_name} · {generateModal.followUp.company} · {generateModal.followUp.follow_up_label}
                </div>
              </div>
              <button className="fu-modal-close" onClick={() => setGenerateModal(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="fu-modal-body">
              <div className="fu-channel-row">
                <label>Channel:</label>
                <select
                  value={generateModal.channel}
                  onChange={(e) => switchGenerateChannel(e.target.value)}
                >
                  <option value="linkedin">LinkedIn</option>
                  <option value="email">Email</option>
                </select>
              </div>

              <div className="fu-instruction">
                Copy this prompt and paste it into your own Claude chat to generate the message.
              </div>

              {generateModal.loading ? (
                <div className="fu-prompt-box fu-prompt-loading">Loading prompt…</div>
              ) : (
                <textarea
                  className="fu-prompt-box"
                  value={generateModal.prompt}
                  readOnly
                />
              )}
            </div>

            <div className="fu-modal-footer">
              <button className="fu-action" onClick={() => setGenerateModal(null)}>
                Close
              </button>
              <button
                className="fu-action fu-action-primary"
                onClick={copyPrompt}
                disabled={generateModal.loading}
              >
                {generateModal.copied
                  ? (<><Check size={14} /> Copied</>)
                  : (<><Copy size={14} /> Copy prompt</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MARK SENT MODAL */}
      {markSentModal && (
        <div className="fu-modal-backdrop" onClick={() => setMarkSentModal(null)}>
          <div className="fu-modal fu-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="fu-modal-header">
              <div>
                <h2>Mark Follow-up Sent</h2>
                <div className="fu-modal-sub">
                  {markSentModal.followUp.prospect_name} · {markSentModal.followUp.follow_up_label}
                </div>
              </div>
              <button className="fu-modal-close" onClick={() => setMarkSentModal(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="fu-modal-body">
              <div className="fu-channel-row">
                <label>Channel sent on:</label>
                <select
                  value={markSentModal.channel}
                  onChange={(e) => setMarkSentModal({ ...markSentModal, channel: e.target.value })}
                >
                  <option value="linkedin">LinkedIn</option>
                  <option value="email">Email</option>
                </select>
              </div>

              <div className="fu-instruction" style={{ marginTop: 12 }}>
                Optional — paste the message you sent (for record-keeping):
              </div>
              <textarea
                className="fu-message-box"
                placeholder="Paste the sent message here, or leave blank…"
                value={markSentModal.messageText}
                onChange={(e) => setMarkSentModal({ ...markSentModal, messageText: e.target.value })}
              />
            </div>

            <div className="fu-modal-footer">
              <button className="fu-action" onClick={() => setMarkSentModal(null)}>
                Cancel
              </button>
              <button className="fu-action fu-action-primary" onClick={submitMarkSent}>
                Mark sent
              </button>
            </div>
          </div>
        </div>
      )}
      {/* REPLIED MODAL */}
      {repliedModal && (
        <div className="fu-modal-backdrop" onClick={() => setRepliedModal(null)}>
          <div className="fu-modal fu-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="fu-modal-header">
              <div>
                <h2>Log Prospect Reply</h2>
                <div className="fu-modal-sub">
                  {repliedModal.followUp.prospect_name} · {repliedModal.followUp.follow_up_label}
                </div>
              </div>
              <button className="fu-modal-close" onClick={() => setRepliedModal(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="fu-modal-body">
              <div className="fu-channel-row">
                <label>Channel reply came on:</label>
                <select
                  value={repliedModal.channel}
                  onChange={(e) => setRepliedModal({ ...repliedModal, channel: e.target.value })}
                >
                  <option value="linkedin">LinkedIn</option>
                  <option value="email">Email</option>
                </select>
              </div>

              <div className="fu-instruction" style={{ marginTop: 12 }}>
                Paste the prospect's reply below. It will be added to the lead notes and a new follow-up will be scheduled.
              </div>
              <textarea
                className="fu-message-box"
                placeholder="Paste the prospect's reply here..."
                value={repliedModal.replyText}
                onChange={(e) => setRepliedModal({ ...repliedModal, replyText: e.target.value })}
                autoFocus
              />
            </div>

            <div className="fu-modal-footer">
              <button className="fu-action" onClick={() => setRepliedModal(null)}>
                Cancel
              </button>
              <button className="fu-action fu-action-primary" onClick={submitReplied}>
                Log reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
